import { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import type { Badge } from '../LibraryDeps'
import { useMediaSrc } from '../useMediaSrc'
import { BadgeChips } from '../vault/BadgeChips'
import { TILE_CHIPS } from '../vault/badges'
import { notePreview } from '../vault/useVaultNotes'
import type { VaultItem } from '../vault/vaultData'

function clock(sec: number): string {
  const s = Math.max(0, Math.round(sec))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = String(s % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
}

const NONE: Badge[] = []

/** Bar heights of a waveform that stays the same for a given audio file. */
function waveOf(id: number, bars = 28): number[] {
  let x = (id || 1) % 2147483647
  return Array.from({ length: bars }, (_, i) => {
    x = (x * 16807) % 2147483647
    const envelope = 0.55 + 0.45 * Math.sin((i / (bars - 1)) * Math.PI)
    return Math.round((0.25 + 0.75 * ((x % 1000) / 1000)) * envelope * 100)
  })
}

/** Audio has no picture on OnlyFans: a tile of its own, so a voice note is never taken for a photo. */
export function AudioArt({ id, compact = false }: { id: number; compact?: boolean }) {
  const bars = waveOf(id, compact ? 12 : 28)
  return (
    <span className={`cpk-audio ${compact ? 'cpk-audio--sm' : ''}`} aria-hidden="true">
      <span className="cpk-audio-icon">
        <FeatherIcon name="headphones" size={compact ? 12 : 20} />
      </span>
      <span className="cpk-audio-wave">
        {bars.map((h, i) => (
          <span key={i} style={{ height: `${h}%` }} />
        ))}
      </span>
    </span>
  )
}

interface Props {
  item: VaultItem
  /** 1-based place in the pick order; 0 = not picked. */
  order: number
  note?: string
  /** The item's Badges (read-only here). */
  badges?: Badge[]
  onClick: (item: VaultItem, e: React.MouseEvent) => void
  onToggle: (item: VaultItem) => void
  onPreview: (item: VaultItem) => void
  /** Registers the tile for lazily loaded notes. */
  observe?: (el: Element | null) => void
}

/**
 * One media tile in the content picker: photos show only the picture, videos
 * their length, audio a waveform tile, and the operator's note and Badges (once
 * loaded) over the bottom of the picture.
 */
export const PickerTile = memo(function PickerTile({ item, order, note = '', badges = NONE, onClick, onToggle, onPreview, observe }: Props) {
  const { t } = useTranslation('contentPicker')
  const audio = item.type === 'audio'
  const { ref, src, blocked } = useMediaSrc(audio ? '' : item.small || item.thumb)
  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      ref.current = el
      observe?.(el)
    },
    [ref, observe],
  )
  const picked = order > 0
  const label = [t(`type.${item.type}`), item.duration > 0 ? clock(item.duration) : '', badges.map((b) => b.name).join(', '), note ? notePreview(note) : '']
    .filter(Boolean)
    .join(' · ')

  return (
    <div
      ref={setRef}
      data-k={item.id}
      className={`cpk-tile cpk-tile--${item.type} ${picked ? 'is-on' : ''}`}
      role="option"
      aria-selected={picked}
      aria-label={label}
      title={note || undefined}
      onClick={(e) => onClick(item, e)}
      onDoubleClick={() => onPreview(item)}
    >
      {audio ? (
        <AudioArt id={item.id} />
      ) : src ? (
        <img className="cpk-tile-img" src={src} alt="" draggable={false} />
      ) : (
        <span className="cpk-tile-ph">{blocked ? t('noPreview') : ''}</span>
      )}
      {item.type === 'video' && (
        <span className="cpk-badge cpk-badge--dur">
          <FeatherIcon name="play" size={10} />
          {item.duration > 0 ? clock(item.duration) : t('type.video')}
        </span>
      )}
      {audio && (
        <span className="cpk-badge cpk-badge--audio">
          <FeatherIcon name="mic" size={10} />
          {item.duration > 0 ? clock(item.duration) : t('type.audio')}
        </span>
      )}
      {item.type === 'gif' && <span className="cpk-badge">GIF</span>}
      {(note || badges.length > 0) && (
        <span className="cpk-note">
          <BadgeChips badges={badges} max={TILE_CHIPS} className="lib-tile-badges" />
          {note && <span className="cpk-note-text">{notePreview(note)}</span>}
        </span>
      )}
      <button
        type="button"
        className="cpk-zoom"
        aria-label={t('preview')}
        title={t('preview')}
        onClick={(e) => {
          e.stopPropagation()
          onPreview(item)
        }}
      >
        <FeatherIcon name="maximize-2" size={13} />
      </button>
      <button
        type="button"
        role="checkbox"
        aria-checked={picked}
        aria-label={t('select')}
        className="cpk-check"
        onClick={(e) => {
          e.stopPropagation()
          onToggle(item)
        }}
      >
        <span className="cpk-check-dot">{picked ? order : ''}</span>
      </button>
    </div>
  )
})
